/**
 * Global assistant tool `memory.remember` (request 0317 / platform spec 0012 §8.5).
 *
 * Confirmation is the only gate (`kind: 'write'`, never `rememberable`). The
 * full text appears in `confirmSummary` — never truncated.
 */

import type { AssistantTool } from '@rammetec/app-kit/assistant'
import {
  AI_MEMORY_TEXT_MAX,
  createAiMemory,
  type AiMemoryScope,
} from '@/services/aiMemoryApi'

export type MemoryRememberScopeLabel = 'minha' | 'organização'

export type MemoryRememberParams = {
  escopo: MemoryRememberScopeLabel
  texto: string
}

export interface AssistantMemoryToolsDeps {
  getOrgId: () => string | null
  /** Org role admin/system — required to annotate organization memory. */
  isOrgAdmin: () => boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toApiScope(escopo: MemoryRememberScopeLabel): AiMemoryScope {
  return escopo === 'organização' ? 'org' : 'user'
}

export function memoryRememberConfirmSummary(params: MemoryRememberParams): string {
  const quoted = `"${params.texto}"`
  if (params.escopo === 'organização') {
    return `Anotar na memória da organização: ${quoted}`
  }
  return `Anotar na minha memória: ${quoted}`
}

/**
 * Factory for `memory.remember`. Pass deps that read latest shell state at call time.
 */
export function createMemoryRememberTool(
  deps: AssistantMemoryToolsDeps,
): AssistantTool<MemoryRememberParams> {
  return {
    name: 'memory.remember',
    label: 'Anotar memória',
    description:
      'Anota uma convenção estável do cliente que se repete entre conversas ' +
      '(padrão de nome, campo que ele sempre usa, regra local). ' +
      'Não use para fato de um registro só, resultado de consulta ou resposta pontual — ' +
      'memória errada vale para todas as conversas da organização.',
    parameters: {
      type: 'object',
      properties: {
        escopo: {
          type: 'string',
          enum: ['minha', 'organização'],
          description:
            "Escopo: 'minha' (só este usuário) ou 'organização' (todos; só administrador).",
        },
        texto: {
          type: 'string',
          description: `Frase curta a anotar (obrigatória, até ${AI_MEMORY_TEXT_MAX} caracteres).`,
        },
      },
      required: ['escopo', 'texto'],
      additionalProperties: false,
    },
    kind: 'write',
    // §8.3 / 0317: writes never enter autoApprovedTools. Do NOT set rememberable.
    validate: (raw) => {
      if (!isRecord(raw)) {
        return { ok: false, error: 'não entendi: parâmetros inválidos' }
      }
      const escopo = raw.escopo
      if (escopo !== 'minha' && escopo !== 'organização') {
        return {
          ok: false,
          error: "não entendi: escopo deve ser 'minha' ou 'organização'",
        }
      }
      if (typeof raw.texto !== 'string') {
        return { ok: false, error: 'não entendi: informe o texto a anotar' }
      }
      const texto = raw.texto.trim()
      if (!texto) {
        return { ok: false, error: 'o texto da memória não pode ficar vazio' }
      }
      if (texto.length > AI_MEMORY_TEXT_MAX) {
        return {
          ok: false,
          error: `o texto da memória pode ter no máximo ${AI_MEMORY_TEXT_MAX} caracteres`,
        }
      }
      if (escopo === 'organização' && !deps.isOrgAdmin()) {
        return {
          ok: false,
          error:
            'Só o administrador pode anotar memória da organização. ' +
            "Use o escopo 'minha' ou peça ao administrador.",
        }
      }
      return { ok: true, params: { escopo, texto } }
    },
    confirmSummary: memoryRememberConfirmSummary,
    run: async (params) => {
      const orgId = deps.getOrgId()
      if (!orgId) {
        return {
          ok: false,
          message: 'Organização não selecionada — não foi possível anotar a memória.',
        }
      }
      try {
        await createAiMemory(orgId, {
          scope: toApiScope(params.escopo),
          product: 'syscolect',
          text: params.texto,
          source: 'assistant',
        })
        return {
          ok: true,
          message:
            params.escopo === 'organização'
              ? 'Memória da organização anotada.'
              : 'Memória pessoal anotada.',
        }
      } catch (error) {
        const message =
          error instanceof Error && error.message.trim()
            ? error.message
            : 'Não foi possível anotar a memória.'
        return { ok: false, message }
      }
    },
  }
}
